import { computed, ref } from "vue";
import { storeToRefs } from "pinia";
import { DateTime } from "luxon";

import { useAuthStore } from "@/stores/Auth";
import { useUserStore } from "@/stores/User";
import { useWebinarsStore } from "@/stores/Webinars";

import { useCategoryFilter } from "@/composables/filters/category-filter";
import { useDateRangeFilter } from "@/composables/filters/date-range-filter";
import { usePagingFilter } from "@/composables/filters/paging-filter";
import { useProductFilter } from "@/composables/filters/product-filter";
import { useTextFilter } from "@/composables/filters/text-filter";

import type { Webinar } from "@tmi/types/entities";
import { webinarKey } from "@/types/products/product-definitions";

/*********************************************************************/
//                       NON-REACTIVE CONSTANTS                      //
/*********************************************************************/
import { consoleLoggerFactory, getDefaultLoggerOptions, ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

/*********************************************************************/
//                 GLOBALLY SHARED REACTIVE VARIABLES                //
/*********************************************************************/

export function useWebinarFilters() {
    const { isAuthenticated } = storeToRefs( useAuthStore() );
    const userStore = useUserStore();
    const webinarsStore = useWebinarsStore();
    const { allWebinars } = storeToRefs( webinarsStore );

    // -------------------------------- FILTER STATE --------------------------------- //
    const filterOutNonFavorites = ref< boolean >( false );
    const hasAnyWebinarResults = computed< boolean >( (): boolean => filteredWebinars.value.length > 0 );
    const webinarsSearchResultsLabel = computed< string >( (): string => `${ filteredWebinars.value.length } webinar${ filteredWebinars.value.length > 1 ? 's' : '' } found` );

    // ------------------------------------ FILTERS ------------------------------------
    // The categories that have been selected to limit the results (inclusive)
    const { filterOutItemsThatDoNotContainSelectedCategories } = useCategoryFilter();

    // The time interval that captures the date range for which to return articles
    const { validDateInterval, isValidDateWithinDateRange } = useDateRangeFilter();

    // The paging filter to manage what is displayed on a per-page basis
    const { filteredContentPage } = usePagingFilter();

    // The products that have been selected to limit the results (inclusive)
    const { filterOutItemsThatDoNotContainSelectedProducts } = useProductFilter();

    const loading = ref< boolean >( false );

    // The search text entered by the user
    const { filterOutItemsThatDoNotContainSearchText } = useTextFilter();

    const filteredWebinarsPage = computed< Webinar[] >(() => {
        return filteredContentPage< Webinar >( filteredWebinars.value );
    });

    const filteredWebinars = computed< Webinar[] >( () => {
        loading.value = true;
        let webinarsAfterFiltering: Webinar[] = [];

        try {
            logger.debug( '[ webinar-filter ].filteredWebinars -> Date Interval filtering (START): ', validDateInterval.value.start?.toLocaleString( DateTime.DATE_FULL ) );
            logger.debug( '[ webinar-filter ].filteredWebinars -> Date Interval filtering (END): ', validDateInterval.value.end?.toLocaleString( DateTime.DATE_FULL ) );

            const composedWebinars: Webinar[] = allWebinars.value.filter( ( webinar: Webinar ) => !filterOutNonFavorites.value || userStore.isFavorited( webinar.contentfulId, webinarKey ) );
            if ( isAuthenticated.value ) {
                logger.debug( `[ webinar-filter ].filteredWebinars -> authenticated, filtering webinars based on short-code access.` );
                webinarsAfterFiltering = composedWebinars
                        // Filter by any categories
                        .filter( ( webinar: Webinar ): boolean => filterOutItemsThatDoNotContainSelectedCategories( webinar.categories ) )
                        // Filter by any products
                        .filter( ( webinar: Webinar ): boolean => filterOutItemsThatDoNotContainSelectedProducts( webinar.products ) )
                        // Filter by date range interval
                        .filter( ( webinar: Webinar ): boolean => isValidDateWithinDateRange( webinar.webinarDate ) )
                        // Filter by text search criteria
                        .filter( ( webinar: Webinar ): boolean => filterOutItemsThatDoNotContainSearchText( [ webinar.text, webinar.title ] ) );
            }
        } finally {
            loading.value  = false;
        }

        return webinarsAfterFiltering;
    } );

    return {
        // T/F based on if filteredWebinars has any items
        hasAnyWebinarResults,
        // '#### results found'
        webinarsSearchResultsLabel,

        // A flag that defines if only the user's favorite webinars that match the given criteria
        // are returned or all that webinars that match the criteria
        filterOutNonFavorites,
        // The current single page sub-collection
        filteredWebinarsPage,
        // All webinars that match the current filter
        filteredWebinars,
        loading
    }
}

export type WebinarFilters = ReturnType< typeof useWebinarFilters >;
